import {
  useMemo, useCallback, useState, useEffect
} from 'react'
import useRouter from 'hooks/useRouter'
import styled from 'styled-components'
import {
  Button, Box, Text, FlexBetween, Dialog, Card, Icon, Flex
} from '@binance/uikit'
import {formatDate, formatNumber} from 'utils/format'
import useSymbolInfo from 'hooks/useSymbolInfo'
import useOrderForm from 'hooks/useOrderForm'
import {TRADE_TYPES, TRADE_SIDES} from 'constants'

const MessageBoxWrap = styled(Card)`
  align-items: center;
  border-radius: 3px;
  color: #848e9c;
  background-color: #2a2f37;

  .close-icon {
    position: absolute;
    right: 7px;
    top: 7px;
    padding: 7px 10px;
    cursor: pointer;
    font-weight: 600;
  }

  .remark-select {
    label {
      cursor: pointer;
      color: #848e9c;
      font-size: 14px;
    }

    input {
      cursor: pointer;
      appearance: none;
      outline: 0;
      height: 14px;
      width: 14px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid #848e9c;
      vertical-align: -1.5px;
      margin-right: 5px;

      :hover {
        /* filter: brightness(0); */
        border: 1px solid #f0b90b;
      }

      :checked {
        background: #f0b90b;
      }

      :checked:after {
        display: block;
      }

      :after {
        content: '';
        position: relative;
        left: 40%;
        top: 20%;
        width: 15%;
        height: 40%;
        border: solid #fff;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
        display: none;
      }
    }
  }

  @media only screen and (max-width: 414px) {
    width: 330px;
  }
`

const StyledFlexBetween = ({
  left, content, pureContent, right, size = 18, places, mb = 6
}) => (
  <FlexBetween
    mb={mb}
    height="16px"
    before={
      <Text.Caption width={130} style={{fontSize: `${size}px`}}>
        {left}
      </Text.Caption>
    }
    text={
      <span style={{fontSize: `${size}px`}}>{pureContent || formatNumber(content, places)}</span>
    }
    after={<Text.Caption style={{fontSize: '14px', alignSelf: 'center'}}>{right}</Text.Caption>}
  />
)

export default function ConfirmDialog (props) {
  const [isChecked, setIsChecked] = useState(false)
  const {
    i18n, type, side, visible, onClose, handleBuy, handleSell
  } = props
  const router = useRouter()
  const {symbol} = router.query
  const {symbolInfo} = useSymbolInfo()
  const {baseAsset, quoteAsset, quantityPrecision} = symbolInfo
  const {
    orderBuyValue,
    marginBuyRequired,
    orderSellValue,
    marginSellRequired,
    buyStopPrice,
    buyPrice,
    buyQty,
    sellStopPrice,
    sellPrice,
    sellQty
  } = useOrderForm(symbol, quoteAsset, type)
  const isBuy = side === TRADE_SIDES.BUY
  const title = isBuy ? i18n.buyLong || 'Buy/Long' : i18n.sellShort || 'Sell/Short'
  const color = isBuy ? 'green' : 'pink'
  const price = isBuy ? buyPrice : sellPrice
  const stopPrice = isBuy ? buyStopPrice : sellStopPrice
  const qty = isBuy ? buyQty : sellQty
  let orderValue = isBuy ? orderBuyValue : orderSellValue
  orderValue = orderValue ? orderValue?.replace(/,/g, '') : 0
  let marginRequired = isBuy ? marginBuyRequired : marginSellRequired
  marginRequired = marginRequired ? marginRequired?.replace(/,/g, '') : 0
  /* const {positionList, balanceData} = usePositionData(symbol)
  const curPosition = Object.values(positionList)
  .filter(el => el.positionSize && Number(el.positionSize) !== 0 && el.symbol === symbol)
  let liquidationPrice = '-'
  if (curPosition?.length > 0) {
    const {positionSize, entryPrice, markPrice} = curPosition[0]
    const pSize = positionSize + (isBuy ? qty : -qty)
    const totalPositionNotionalValue = positionNotionalValueFn({positionSize: pSize, markPrice})
    liquidationPrice = liquidationPriceFn(
      Number(totalPositionNotionalValue),
      Number(balanceData.walletBalance - (price * qty)),
      Number(pSize),
      Number(entryPrice || 0)
    )
    liquidationPrice = liquidationPrice > 0 ? liquidationPrice : ''
  } */

  const handleTrade = useCallback(() => {
    if (isChecked) {
      window.localStorage.setItem(`dialog_${type}_${side}`, isChecked)
    }
    onClose()
    if (isBuy) {
      handleBuy(true)
    } else {
      handleSell(true)
    }
  }, [handleBuy, handleSell, isBuy, isChecked, onClose, side, type])

  useEffect(() => {
    if (visible) {
      setIsChecked(false)
    }
  }, [visible])

  return useMemo(
    () => (
      <Dialog visible={visible}>
        <MessageBoxWrap width={352} p={32}>
          <div className="close-icon" onClick={onClose}>
            <Icon color="#84819c" hover="#A9B0BA" size={12} name="close1" />
          </div>
          <Text.Base color={color} mt={4} fontSize={22} fontWeight={500}>
            {title} <span style={{color: '#fff'}}>{symbol}</span>
          </Text.Base>
          <Text.Base color="#848E9C" mt={4} fontSize={14}>
            <span style={{color: '#F0B90B', marginRight: '8px'}}>
              {type === TRADE_TYPES.STOP ? i18n.stopLimit || 'Stop-Limit' : i18n[type]}
            </span>
            {formatDate(new Date())}
          </Text.Base>
          <Box mt={8} pb={4} width="100%" borderBottom="1px solid rgba(255, 255, 255, 0.1)">
            {type === TRADE_TYPES.STOP && (
              <StyledFlexBetween
                left={i18n.stopPriceTitle || 'Stop Price'}
                content={stopPrice}
                right={quoteAsset}
              />
            )}
            {type === TRADE_TYPES.MARKET ? (
              <StyledFlexBetween
                left={i18n.priceTitle || 'Price'}
                pureContent={i18n.market || 'Market'}
                right=""
              />
            ) : (
              <StyledFlexBetween
                left={i18n.priceTitle || 'Price'}
                content={price}
                right={quoteAsset}
              />
            )}
            <StyledFlexBetween
              left={i18n.orderQtyTitle || 'Order Qty'}
              content={qty}
              places={quantityPrecision}
              right={baseAsset}
            />
          </Box>
          <Box mt={8} mb={30} width="100%">
            <StyledFlexBetween
              size="14"
              mb={4}
              left={i18n.marginRequired || 'Margin Required'}
              content={marginRequired}
              right={quoteAsset}
            />
            {type !== TRADE_TYPES.MARKET && (
              <StyledFlexBetween
                size="14"
                mb={4}
                left={i18n.orderValue || 'Order Value'}
                content={orderValue}
                right={quoteAsset}
              />
            )}
            {/* <StyledFlexBetween size="14" left={<>
              Liquidation Price&nbsp;
              <Tooltip noBorder label={<Icon
                color="#848e9c"
                hover="#a9b0ba"
                size={16}
                name="information-o"
              />} tip={tipMsgData.liquidationPriceTip} />
            </>} content={liquidationPrice} right={quoteAsset} /> */}
          </Box>
          <Flex width="100%" mb={7} className="remark-select">
            <label>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => {
                  setIsChecked(!isChecked)
                }}
              />
              {i18n.dontAskAgain || 'Don’t ask me again'}
            </label>
          </Flex>
          <Flex width="100%">
            <Button ml={0} mr={5} height={38} width="100%" outline bg="#fff" onClick={onClose}>
              {i18n.cancelBtnText || 'Cancel'}
            </Button>
            <Button height={40} width="100%" bg={color} onClick={handleTrade}>
              {title}
            </Button>
          </Flex>
        </MessageBoxWrap>
      </Dialog>
    ),
    [
      visible,
      onClose,
      color,
      title,
      symbol,
      type,
      stopPrice,
      quoteAsset,
      price,
      qty,
      quantityPrecision,
      baseAsset,
      orderValue,
      marginRequired,
      isChecked,
      i18n,
      handleTrade
    ]
  )
}
